import { Link, useLocation, useNavigate } from "react-router-dom";
import { Headphones, Gift, Crown, Users, Sparkles, Trophy, MessageCircleMore, Headset, Ticket, Send, Heart } from "lucide-react";
import { toast } from "sonner";
import { useSiteCategories } from "../../hooks/useSiteCategories";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import { useAuth } from "../../contexts/AuthContext";
import React, { useState } from "react";
import LiveChatPanel from "./LiveChatPanel";
import ExclusiveIcon from "./ExclusiveIcon";

// icon hints for secondary quick-link items (falls back to Gift)
const SECONDARY_ICONS = {
  promotions: { icon: Gift, color: "#ef4444" },
  vip: { icon: Crown, color: "#ffc933" },
  referral: { icon: Users, color: "#19a55c" },
};

export const LeftSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { primary, secondary } = useSiteCategories();
  return (
    <aside
      data-testid="left-sidebar"
      className="hidden lg:flex flex-col fixed left-0 top-[56px] lg:top-[64px] bottom-0 w-[88px] xl:w-[112px] z-30 border-r border-white/5 overflow-y-auto"
      style={{ background: "#070b11" }}
    >
      <nav className="flex-1 py-3">
        {primary.map((m) => {
          const slug = m.slug || m.id;
          const active =
            m.to === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(m.to);
          const isFav = slug === "favourite";
          const handleClick = (e) => {
            if (isFav && !user) {
              e.preventDefault();
              toast.info("Please log in to view your favourites");
              navigate("/login");
            }
          };
          return (
            <Link
              key={slug}
              to={m.to}
              onClick={handleClick}
              data-testid={`leftbar-${slug}`}
              className={`relative flex flex-col items-center gap-1 px-2 py-3 mx-2 rounded-lg transition-all ${
                active
                  ? "bg-[#19a55c]/15 text-white"
                  : "text-white/65 hover:text-white hover:bg-white/5"
              }`}
            >
              {active && (
                <span
                  aria-hidden
                  className="absolute -left-2 top-2 bottom-2 w-1 rounded-full bg-[#19a55c]"
                />
              )}
              {slug === "exclusive" ? (
                <ExclusiveIcon
                  size={32}
                  active={active}
                  className={`transition-transform ${active ? "scale-110" : ""}`}
                />
              ) : slug === "favourite" ? (
                <Heart
                  size={28}
                  fill="#c89b3c"
                  stroke="#c89b3c"
                  strokeWidth={1.5}
                  className={`transition-transform ${active ? "scale-110" : ""}`}
                />
              ) : (
                m.icon && (
                  <img
                    src={m.icon}
                    alt=""
                    className={`h-7 w-7 transition-transform ${
                      active ? "scale-110" : "opacity-80"
                    }`}
                    onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                  />
                )
              )}
              <span
                className={`text-[10px] xl:text-xs font-bold text-center leading-tight ${
                  active ? "text-[#19a55c]" : ""
                }`}
                style={isFav ? { color: active ? "#c89b3c" : "#c89b3c" } : undefined}
              >
                {m.label}
              </span>
            </Link>
          );
        })}
      </nav>

      {secondary.filter((s) => ["promotions", "vip", "referral"].includes(s.slug)).length > 0 && (
        <div className="border-t border-white/5 py-2">
          {secondary
            .filter((s) => ["promotions", "vip", "referral"].includes(s.slug))
            .map((q) => {
              const hint = SECONDARY_ICONS[q.slug] || SECONDARY_ICONS.promotions;
              const Icon = hint.icon;
              return (
                <Link
                  key={q.slug}
                  to={q.to}
                  data-testid={`leftbar-${q.slug}`}
                  className="flex flex-col items-center gap-1 px-2 py-2.5 mx-2 rounded-lg text-white/65 hover:text-white hover:bg-white/5"
                >
                  <Icon size={18} style={{ color: hint.color }} />
                  <span className="text-[10px] xl:text-xs font-semibold text-center">
                    {q.label}
                  </span>
                </Link>
              );
            })}
        </div>
      )}
    </aside>
  );
};

export const FloatingSupport = () => {
  const navigate = useNavigate();
  const { telegramSupport, liveChat } = useSiteSettings();
  // Hide the in-house Max_Ai bubble when EITHER:
  //  - the admin turned off the floating-button toggle in Live Chat config, OR
  //  - the AI assistant itself is disabled in AI Live Chat config, OR
  //  - a 3rd-party live-chat widget is enabled — in which case that provider's
  //    own bubble takes over the SAME spot (positioned via LiveChatLoader CSS).
  const thirdPartyOn = !!liveChat?.enabled;
  const showFloatingChat =
    liveChat?.show_floating_button !== false &&
    liveChat?.ai_enabled !== false &&
    !thirdPartyOn;
  const [chatOpen, setChatOpen] = useState(false);
  const [unread, setUnread] = useState(() => {
    try { return parseInt(localStorage.getItem("chat_unread_admin") || "0", 10) || 0; } catch { return 0; }
  });
  // Listen to cross-component unread counter changes
  React.useEffect(() => {
    const handler = () => {
      try { setUnread(parseInt(localStorage.getItem("chat_unread_admin") || "0", 10) || 0); } catch { setUnread(0); }
    };
    window.addEventListener("chat-unread-changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("chat-unread-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  // Also open the in-app live chat panel when other components (e.g. SiteHeader's
  // "24/7 Live Chat" button) dispatch a global event. This keeps a single source
  // of truth for the panel state instead of duplicating it across components.
  React.useEffect(() => {
    const openHandler = () => setChatOpen(true);
    window.addEventListener("open-live-chat-panel", openHandler);
    return () => window.removeEventListener("open-live-chat-panel", openHandler);
  }, []);
  return (
    <>
      {/* ============================================================
         DAILY FREE SPIN — unmistakable lucky-wheel icon
         (colorful prize sectors · center hub · pointer · "SPIN" tag)
         ============================================================ */}
      <button
        type="button"
        onClick={() => navigate("/profile/free-spin")}
        data-testid="floating-spin"
        aria-label="Daily Free Spin"
        className="fixed bottom-36 lg:bottom-32 right-3 sm:right-4 z-40 h-14 w-14 sm:h-16 sm:w-16 rounded-full group transition-transform hover:scale-110"
        style={{ animation: "spinFloatBob 3.5s ease-in-out infinite" }}
      >
        {/* Soft outer halo (slow pulse) */}
        <span
          aria-hidden
          className="absolute inset-[-10px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(251,191,36,0.55) 0%, rgba(251,191,36,0) 65%)",
            animation: "spinIconHalo 2.4s ease-in-out infinite",
          }}
        />

        {/* Outer gold ring (static, frames the wheel) */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "conic-gradient(from 0deg, #fbbf24, #f59e0b, #fde68a, #f59e0b, #fbbf24)",
            boxShadow:
              "0 16px 36px rgba(251,191,36,0.55), 0 0 0 2px rgba(251,191,36,0.7), inset 0 1px 0 rgba(255,255,255,0.55)",
          }}
        />

        {/* Rotating colored prize-sectors wheel — the ACTUAL wheel */}
        <span
          aria-hidden
          className="absolute inset-[4px] rounded-full overflow-hidden"
          style={{
            background:
              "conic-gradient(from 0deg, #ef4444 0deg 45deg, #f59e0b 45deg 90deg, #facc15 90deg 135deg, #10b981 135deg 180deg, #3b82f6 180deg 225deg, #8b5cf6 225deg 270deg, #ec4899 270deg 315deg, #f97316 315deg 360deg)",
            animation: "spinIconRotate 6s linear infinite",
            boxShadow: "inset 0 0 0 1.5px rgba(255,255,255,0.55)",
          }}
        >
          {/* White divider lines between sectors (counter-rotating w/ wheel) */}
          <span
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "repeating-conic-gradient(from 0deg, rgba(255,255,255,0) 0deg 44deg, rgba(255,255,255,0.85) 44deg 45deg)",
            }}
          />
        </span>

        {/* Center hub — gold disc with star + "SPIN" */}
        <span
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center z-10"
          style={{
            background:
              "radial-gradient(circle at 32% 28%, #fffbeb 0%, #fde68a 40%, #b45309 100%)",
            boxShadow:
              "0 0 0 2px rgba(180,83,9,0.6), inset 0 -2px 4px rgba(120,53,15,0.5), inset 0 1px 3px rgba(255,255,255,0.7), 0 2px 6px rgba(0,0,0,0.35)",
          }}
        >
          <Sparkles
            size={14}
            strokeWidth={2.6}
            className="text-[#7c2d12] drop-shadow-[0_1px_0_rgba(255,255,255,0.7)]"
          />
        </span>

        {/* Top pointer pin (large, unmistakable) */}
        <span
          aria-hidden
          className="absolute -top-1.5 left-1/2 -translate-x-1/2 pointer-events-none z-20"
          style={{
            width: 0,
            height: 0,
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderTop: "12px solid #fffbeb",
            filter:
              "drop-shadow(0 1px 2px rgba(0,0,0,0.7)) drop-shadow(0 0 4px rgba(251,191,36,0.7))",
          }}
        />

        {/* "SPIN" label badge — sits at the bottom, fully readable */}
        <span
          className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 px-1.5 py-[1px] rounded-full text-[8px] font-black tracking-wider z-20 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, #b91c1c 0%, #7f1d1d 100%)",
            color: "#fff7d6",
            boxShadow:
              "0 2px 6px rgba(0,0,0,0.5), 0 0 0 1.5px rgba(254,243,199,0.7), inset 0 1px 0 rgba(255,255,255,0.4)",
            letterSpacing: "0.08em",
          }}
        >
          SPIN
        </span>

        {/* Notification badge */}
        <span className="absolute -top-1 -right-0.5 h-4 w-4 rounded-full bg-rose-500 ring-2 ring-[#0a1119] flex items-center justify-center text-[9px] font-extrabold text-white shadow-md z-20">
          <span className="absolute inset-0 rounded-full bg-rose-500 animate-ping opacity-70" />
          <span className="relative">1</span>
        </span>

        {/* Hover tooltip */}
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap px-3 py-1.5 rounded-md bg-[#0e1620] border border-amber-400/40 text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none hidden lg:flex items-center gap-1.5 shadow-xl">
          <Sparkles size={12} className="text-amber-300" />
          Daily Free Spin
        </span>
      </button>

      {/* ============================================================
         (REMOVED) Old 24/7 LIVE CHAT FAB + Max_Ai LiveChatPanel
         The site now uses the single homepage-only LiveSupportWidget
         (mounted via App.js → HomepageOnlyChat). Keeping a second chat
         FAB here used to collide with the mobile bottom nav and the
         Profile tap-target on small screens.
         ============================================================ */}

      {/* ============================================================
         TELEGRAM SUPPORT — admin-toggleable deep-link FAB
         Visually positioned BELOW the Live Chat FAB (smaller bottom value
         on desktop). On mobile we move it ABOVE the Free Spin button to
         avoid overlapping the mobile bottom-bar.
         Only renders when admin has enabled it AND a URL is configured.
         ============================================================ */}
      {telegramSupport?.enabled && telegramSupport?.url && (
        <a
          href={telegramSupport.url}
          target={telegramSupport.open_in_new_tab ? "_blank" : "_self"}
          rel={telegramSupport.open_in_new_tab ? "noopener noreferrer" : undefined}
          data-testid="floating-telegram"
          aria-label={telegramSupport.label || "Telegram Support"}
          className="fixed bottom-[208px] lg:bottom-[176px] right-3 sm:right-4 z-40 h-12 w-12 sm:h-14 sm:w-14 rounded-full group hover:scale-110 transition-transform"
        >
          {/* Soft pulsing halo */}
          <span
            aria-hidden
            className="absolute inset-[-8px] rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(34,158,217,0.55) 0%, rgba(34,158,217,0) 65%)",
              animation: "spinIconHalo 2.6s ease-in-out infinite",
            }}
          />
          {/* Rotating gradient ring */}
          <span
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "conic-gradient(from 0deg, #38bdf8, #0ea5e9, #0284c7, #0ea5e9, #38bdf8)",
              animation: "spinIconRotate 9s linear infinite",
              boxShadow:
                "0 14px 32px rgba(14,165,233,0.55), 0 0 0 1.5px rgba(56,189,248,0.7)",
            }}
          />
          {/* Glossy sky-blue inner disc with paper-plane icon */}
          <span
            className="absolute inset-[2px] rounded-full flex items-center justify-center overflow-hidden"
            style={{
              background:
                "radial-gradient(circle at 32% 26%, #7dd3fc 0%, #0ea5e9 45%, #075985 100%)",
              boxShadow:
                "inset 0 -4px 10px rgba(0,0,0,0.35), inset 0 2px 6px rgba(255,255,255,0.45)",
            }}
          >
            {/* Top-left highlight */}
            <span
              aria-hidden
              className="absolute -top-1 -left-1 h-5 w-5 rounded-full opacity-80 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 70%)",
              }}
            />
            <Send
              size={20}
              strokeWidth={2.2}
              className="relative text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)] -ml-0.5"
            />
          </span>
          {/* Hover tooltip (desktop only) */}
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap px-3 py-1.5 rounded-md bg-[#0e1620] border border-sky-400/40 text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none hidden lg:flex flex-col items-start gap-0.5 shadow-xl">
            <span className="inline-flex items-center gap-1.5">
              <Send size={12} className="text-sky-300" />
              {telegramSupport.label || "Telegram Support"}
            </span>
            {telegramSupport.tagline && (
              <span className="text-[10px] font-normal text-white/55">
                {telegramSupport.tagline}
              </span>
            )}
          </span>
        </a>
      )}

      {/* LiveChatPanel removed — site now uses single homepage-only LiveSupportWidget */}
    </>
  );
};
